import EntityMultiSelect, { selectedEntityIds } from '../../../../shared/components/EntityMultiSelect';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from '../../../../shared/assets/AppIcons';
import BaseDealDetailDialog from '@/modules/deals/shared/components/BaseDealDetailDialog';
import { getCompletedDealSettlement } from '@/modules/deals/services';
import { IconActionButton } from '@/shared/components/patterns';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { settleDealPayments } from '../../../accounting/services/deal-accounting-links';
import EntityDetailDialog from '../../../../shared/components/EntityDetailDialog';

function formatSettlementAmount(amount) {
  if (amount === null || amount === undefined) return null;
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function getSettlementBadgeClass(status) {
  return status === 'saldato'
    ? 'bg-[var(--success-soft)] text-[var(--success-text)] border-[var(--success-border)]'
    : 'bg-[var(--warning-soft)] text-[var(--warning-text)] border-[var(--warning-border)]';
}

function getPaymentPlanSummary(deal) {
  const installmentCount = Number(deal?.installmentCount ?? deal?.dilazionamento?.rate ?? 1);
  const dueDates = deal?.installmentDueDates || deal?.dilazionamento?.scadenze || [];
  return {
    label: installmentCount > 1 ? `${installmentCount} rate` : 'Pagamento unico',
    dueDate: deal?.paymentExpectedDate || deal?.paymentDate || deal?.dataPagamento || dueDates[0] || '',
  };
}

const CompletedDetailDialog = ({
  deal,
  isOpen,
  onClose,
  focusComments = false,
  onOpenClient,
  onOpenStaff,
  onCreateInvoice,
  onOpenAccounting,
  onDelete,
  onUpdateStaff,
  staff = [],
  staffCatalogStatus = 'empty',
  canManageDealDestructiveActions = false,
}) => {
  const [settlementState, setSettlementState] = useState({
    status: 'idle',
    data: null,
    message: '',
  });
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const selectedStaffId = selectedStaffIds[0] || '';
  const [isStaffEditing, setIsStaffEditing] = useState(false);
  const [staffSaveState, setStaffSaveState] = useState({
    status: 'idle',
    message: '',
  });
  const staffSaveLockRef = useRef(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settling, setSettling] = useState(false);
  const [settleError, setSettleError] = useState('');
  const [revision, setRevision] = useState(0);
  const settleLock = useRef(false);

  useEffect(() => {
    if (!isOpen || !deal?.id) {
      setSelectedStaffIds([]);
      setIsStaffEditing(false);
      setStaffSaveState({ status: 'idle', message: '' });
      staffSaveLockRef.current = false;
      return;
    }

    setSelectedStaffIds(selectedEntityIds(deal, 'staff'));
    setIsStaffEditing(false);
    setStaffSaveState({ status: 'idle', message: '' });
    staffSaveLockRef.current = false;
  }, [deal?.id, deal?.staffId, isOpen]);

  useEffect(() => {
    if (!isOpen || !deal?.id) {
      setSettlementState({ status: 'idle', data: null, message: '' });
      return undefined;
    }

    let active = true;
    async function loadSettlement() {
      setSettlementState({ status: 'loading', data: null, message: '' });
      const result = await getCompletedDealSettlement(deal.id);
      if (!active) return;
      setSettlementState(result.ok
        ? { status: 'success', data: result.data, message: '' }
        : {
            status: result.error === 'SETTLEMENT_NOT_FOUND' ? 'not_found' : 'error',
            data: null,
            message: result.message,
          });
    }
    loadSettlement();
    return () => {
      active = false;
    };
  }, [deal?.id, isOpen, revision]);

  const dealWithSettlement = useMemo(() => {
    if (!deal) return deal;
    return {
      ...deal,
      paymentStatus: settlementState.status === 'success'
        ? settlementState.data.status
        : deal.paymentStatus || null,
    };
  }, [deal, settlementState]);

  async function handleStaffSave() {
    if (
      staffSaveLockRef.current ||
      !deal?.id ||
      !selectedStaffId ||
      typeof onUpdateStaff !== 'function'
    ) {
      return;
    }

    staffSaveLockRef.current = true;
    setStaffSaveState({ status: 'saving', message: '' });
    try {
      const result = await onUpdateStaff(deal.id, selectedStaffIds);
      if (!result?.ok) {
        setStaffSaveState({
          status: 'error',
          message: result?.message || 'Impossibile aggiornare il responsabile.',
        });
        return result;
      }

      setStaffSaveState({
        status: 'success',
        message: result.message || 'Responsabile aggiornato.',
      });
      setIsStaffEditing(false);
      return result;
    } finally {
      staffSaveLockRef.current = false;
    }
  }

  const currentStaffId = dealWithSettlement?.staffId ? String(dealWithSettlement.staffId) : '';
  const currentStaff = staff.find((member) => String(member.id) === currentStaffId);
  const canSaveStaff =
    selectedStaffId &&
    JSON.stringify(selectedStaffIds) !== JSON.stringify(selectedEntityIds(dealWithSettlement, 'staff')) &&
    staffCatalogStatus === 'available' &&
    staffSaveState.status !== 'saving';
  const hasGeneratedInvoice = Boolean(settlementState.data?.invoiceIds?.length);
  const paymentPlanSummary = getPaymentPlanSummary(dealWithSettlement || deal);
  const staffEditActions = typeof onUpdateStaff === 'function' && staffCatalogStatus === 'available'
    ? isStaffEditing ? (
      <>
        <Button type="button" onClick={handleStaffSave} disabled={!canSaveStaff}>
          {staffSaveState.status === 'saving' ? 'Salvataggio…' : 'Salva responsabile'}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={staffSaveState.status === 'saving'}
          onClick={() => {
            setSelectedStaffIds(selectedEntityIds(dealWithSettlement, 'staff'));
            setStaffSaveState({ status: 'idle', message: '' });
            setIsStaffEditing(false);
          }}
        >
          Annulla
        </Button>
      </>
    ) : (
      <IconActionButton
        iconKey="actions.edit"
        label="Modifica responsabile"
        onClick={() => setIsStaffEditing(true)}
      />
    )
    : null;

  return (
    <><BaseDealDetailDialog focusComments={focusComments}
      key={revision}
      deal={dealWithSettlement}
      isOpen={isOpen}
      onClose={onClose}
      onOpenClient={onOpenClient}
      onOpenStaff={onOpenStaff}
      dealType="completato"
      staff={staff}
      allowEditing={false}
      allowTypeChange={false}
      customEditActions={staffEditActions}
      primaryActions={deal && (settlementState.status === 'not_found' || settlementState.data?.status === 'da_saldare') ? (
        <IconActionButton iconKey="actions.complete" tone="success" label="Salda affare"
          onClick={() => { setSettleError(''); setSettleOpen(true); }} />
      ) : null}
      ordinaryActions={deal ? (
        hasGeneratedInvoice ? (
          <IconActionButton
            iconKey="actions.preview"
            label="Vai a Contabilità"
            onClick={() => onOpenAccounting?.(deal)}
          />
        ) : (
          <IconActionButton
            iconKey="actions.add"
            label="Crea fattura"
            onClick={() => onCreateInvoice?.(dealWithSettlement || deal)}
          />
        )
      ) : null}
      dangerActions={deal && canManageDealDestructiveActions ? (
        <IconActionButton
          iconKey="actions.delete"
          label="Elimina affare completato"
          tone="danger"
          onClick={() => onDelete?.(deal.id)}
        />
      ) : null}
      extraContent={deal ? (
        <div className="deal-detail__completed-grid">
          <section className="entity-detail-dialog__card">
            <h3>Responsabile</h3>
            {isStaffEditing ? (
              <EntityMultiSelect label="Responsabili affare completato" options={staff} value={selectedStaffIds}
                onChange={setSelectedStaffIds} disabled={staffSaveState.status === 'saving'} />
            ) : (
              <p>{currentStaff?.name || dealWithSettlement?.assignee || 'Nessun responsabile assegnato'}</p>
            )}
            {staffCatalogStatus === 'empty' ? (
              <p role="alert" className="entity-detail-dialog__notice wt-inline-error">Catalogo staff vuoto.</p>
            ) : null}
            {staffCatalogStatus === 'unavailable' ? (
              <p role="alert" className="entity-detail-dialog__notice wt-inline-error">
                Catalogo staff non disponibile. La modifica e la navigazione sono disabilitate.
              </p>
            ) : null}
            {staffSaveState.status === 'error' || staffSaveState.status === 'success' ? (
              <p
                className={staffSaveState.status === 'error' ? 'deal-detail__error wt-inline-error' : 'deal-detail__success'}
                role={staffSaveState.status === 'error' ? 'alert' : 'status'}
              >
                {staffSaveState.message}
              </p>
            ) : null}
          </section>

          <section className="entity-detail-dialog__card">
            <h3>Settlement reale</h3>
            {settlementState.status === 'loading' ? (
              <div className="deal-detail__inline-state">
                <Loader2 size={16} className="animate-spin" />
                Caricamento da accounting…
              </div>
            ) : null}
            {settlementState.status === 'success' ? (
              <div className="deal-detail__settlement">
                <Badge variant="outline" className={getSettlementBadgeClass(settlementState.data.status)}>
                  {settlementState.data.status === 'saldato' ? 'Saldato' : 'Da saldare'}
                </Badge>
                <div>
                  <span>Totale</span>
                  <strong>{formatSettlementAmount(settlementState.data.totalAmount) || '—'}</strong>
                </div>
                <div>
                  <span>Incassato</span>
                  <strong>{formatSettlementAmount(settlementState.data.receivedAmount) || '—'}</strong>
                </div>
              </div>
            ) : null}
            {settlementState.status === 'not_found' || settlementState.status === 'error' ? (
              <div role="alert" className={settlementState.status === 'error' ? 'deal-detail__error wt-inline-error' : 'entity-detail-dialog__notice wt-inline-error'}>
                <AlertCircle size={16} aria-hidden="true" />
                <span>{settlementState.message || 'Settlement non disponibile.'}</span>
              </div>
            ) : null}
          </section>

          <section className="entity-detail-dialog__card">
            <h3>Piano pagamento ereditato</h3>
            <strong>{paymentPlanSummary.label}</strong>
            <p>Scadenza prevista: {paymentPlanSummary.dueDate || 'default di sistema'}</p>
            {hasGeneratedInvoice ? <p className="deal-detail__success">Fattura già generata</p> : null}
          </section>
        </div>
      ) : null}
    />
    <EntityDetailDialog isOpen={settleOpen} title="Saldare l’affare?" subtitle={deal?.name}
      onClose={() => { if (!settling) setSettleOpen(false); }}
      primaryActions={<IconActionButton iconKey="actions.complete" tone="success" label="Conferma saldo" disabled={settling}
        onClick={async () => {
          if (settleLock.current) return;
          settleLock.current = true;
          setSettling(true); setSettleError('');
          try { await settleDealPayments(deal.id); setSettleOpen(false); setRevision((value) => value + 1); }
          catch (error) { setSettleError(error.message); }
          finally { settleLock.current = false; setSettling(false); }
        }} />}
      actions={<IconActionButton iconKey="actions.close" label="Annulla saldo" disabled={settling} onClick={() => setSettleOpen(false)} />}>
      <p>Gli incassi mancanti verranno creati e le rate ancora da incassare saranno registrate con la data di oggi.</p>
      {settleError ? <p role="alert" className="wt-inline-error">{settleError}</p> : null}
      {settling ? <p role="status">Registrazione incassi…</p> : null}
    </EntityDetailDialog></>
  );
};

export default CompletedDetailDialog;
